import React, { useMemo } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import { useSidebar } from "../../contexts/SidebarContext";
import useAdminUser from "../../hooks/useAdminUser";
import { getFirstAccessibleAdminPath } from "../../utils/adminNavigation";

import RequireAdminModuleAccess from "./adminShell/components/RequireAdminModuleAccess";
import AdminLoadingState from "./adminShell/ui/AdminLoadingState";
import { adminRoutes } from "./adminShell/data/adminRoutes";
import { ADMIN_SIDEBAR_MARGINS } from "./adminShell/data/adminShellConfig";

export default function MainContent() {
  const { isCollapsed } = useSidebar();
  const { user: effectiveUser, permissionsLoaded } = useAdminUser();

  const firstAccessiblePath = useMemo(() => {
    if (!permissionsLoaded) return null;
    if (!effectiveUser) return "/login";
    return getFirstAccessibleAdminPath(effectiveUser) || "/";
  }, [effectiveUser, permissionsLoaded]);

  const sidebarMargin = isCollapsed
    ? ADMIN_SIDEBAR_MARGINS.collapsed
    : ADMIN_SIDEBAR_MARGINS.expanded;

  return (
    <main
      className={`min-h-screen w-full transition-[margin] duration-300 ${sidebarMargin}`}
    >
      <div className="relative min-h-screen overflow-x-hidden bg-slate-100 pt-[104px] text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 right-[-120px] h-96 w-96 rounded-full bg-sky-300/35 blur-3xl dark:bg-sky-500/10" />
          <div className="absolute left-[-160px] top-[22%] h-96 w-96 rounded-full bg-cyan-300/25 blur-3xl dark:bg-cyan-500/10" />
          <div className="absolute bottom-[-160px] right-[18%] h-96 w-96 rounded-full bg-indigo-300/25 blur-3xl dark:bg-indigo-500/10" />
        </div>

        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(15,23,42,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.04)_1px,transparent_1px)] bg-[size:38px_38px] opacity-40 dark:opacity-10" />

        <div className="relative min-h-screen px-3 pb-8 sm:px-4 lg:px-6">
          {!permissionsLoaded ? (
            <AdminLoadingState />
          ) : (
            <Routes>
              <Route
                index
                element={<Navigate to={firstAccessiblePath || "/"} replace />}
              />

              {adminRoutes.map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    <RequireAdminModuleAccess
                      user={effectiveUser}
                      moduleKey={route.moduleKey}
                      permissionsLoaded={permissionsLoaded}
                    >
                      {route.element}
                    </RequireAdminModuleAccess>
                  }
                />
              ))}

              <Route
                path="*"
                element={<Navigate to={firstAccessiblePath || "/"} replace />}
              />
            </Routes>
          )}
        </div>
      </div>
    </main>
  );
}